/**
 * What to call the thing on screen: a folder, or a bucket.
 *
 * A scope is `(account, bucket, prefix)`, so half the scopes this product can
 * hold are folders. The interface called every one of them "this bucket" -
 * "Search this bucket", "Also for this bucket", "Register this bucket's read
 * access" - while the operator was looking at a folder inside one.
 *
 * That is the trap the folder-scope contract names outright: two scopes over
 * one bucket are genuinely separate, and the UI has to make obvious which one
 * is being looked at "or a person will think they are seeing everything".
 * Naming the scope in the rail fixed the label and left every sentence beneath
 * it saying bucket, which is the same claim again in prose.
 */

/** `folder` when the scope is a prefix inside a bucket, `bucket` otherwise. */
export function scopeNoun(prefix: string | null | undefined): 'folder' | 'bucket' {
  return (prefix ?? '').trim() === '' ? 'bucket' : 'folder';
}

/** "this folder" / "this bucket", for sentences that point at the scope. */
export function thisScope(prefix: string | null | undefined): string {
  return `this ${scopeNoun(prefix)}`;
}

/** "This folder" / "This bucket", where the sentence starts with it. */
export function thisScopeCapitalised(prefix: string | null | undefined): string {
  return `This ${scopeNoun(prefix)}`;
}

/** "this folder’s" / "this bucket’s". */
export function thisScopePossessive(prefix: string | null | undefined): string {
  return `this ${scopeNoun(prefix)}’s`;
}
